import React, { useState, useEffect } from 'react';
import { Search, MessageCircle } from 'lucide-react';
import ConversationItem from './ConversationItem';
import EmptyState from './EmptyState';
import LoadingIndicator from './LoadingIndicator';
import { motion } from 'framer-motion';
import { filterConversations, sortConversations } from '../utils/messageProcessor';

/**
 * Component that displays the list of conversations with search and favorite functionality
 */
const ConversationList = ({
  conversations = [],
  loading = false,
  selectedConversationId = null,
  onSelectConversation,
  onNewChat,
  onDeleteConversation,
  onArchiveConversation,
  onToggleFavorite,
  favorites = [],
  sortBy = 'newest',
  showFavoritesOnly = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredConversations, setFilteredConversations] = useState([]);
  
  // Update filtered conversations when search term, sort order, favorites, or conversations change
  useEffect(() => {
    // First filter by search term
    let filtered = filterConversations(conversations, searchTerm);
    
    // Then filter by favorites if necessary
    if (showFavoritesOnly) {
      filtered = filtered.filter(conv => favorites.includes(conv.id));
    }
    
    // Finally sort
    const sorted = sortConversations(filtered, sortBy);
    setFilteredConversations(sorted);
  }, [conversations, searchTerm, sortBy, favorites, showFavoritesOnly]);
  
  return (
    <div className="w-full md:w-80 border-r border-slate-700/50 flex flex-col h-full bg-slate-800/50">
      {/* Search header */}
      <div className="p-3 border-b border-slate-700/50 bg-slate-800/70">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-700/70 border border-slate-600/50 rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 focus:border-emerald-500/50"
          />
        </div>
      </div>

      {/* Conversation list with improved scrolling */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
        {loading ? (
          <LoadingIndicator />
        ) : filteredConversations.length === 0 ? (
          <EmptyState 
            searchTerm={searchTerm}
            title={searchTerm ? "No matching conversations" : 
                  showFavoritesOnly ? "No favorite conversations" : "No conversations yet"}
            description={searchTerm ? `No results for "${searchTerm}"` : 
                        showFavoritesOnly ? "Star conversations to add them to your favorites" : 
                        "Start a new chat to begin your conversations"}
            actionButton={
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={onNewChat}
                className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors shadow-md"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                <span>Start New Chat</span>
              </motion.button>
            }
          />
        ) : (
          filteredConversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              isSelected={selectedConversationId === conversation.id}
              onClick={() => onSelectConversation(conversation)}
              onDelete={onDeleteConversation}
              onArchive={onArchiveConversation}
              onToggleFavorite={onToggleFavorite}
              isFavorite={favorites.includes(conversation.id)}
            />
          ))
        )}
      </div>
                    
      {/* Bottom actions */}
      <div className="p-3 border-t border-slate-700/50 bg-slate-800/90">
        <button
          onClick={onNewChat}
          className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md flex items-center justify-center transition-colors"
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          <span>New Chat</span>
        </button>
      </div>
    </div> 
  );
};

export default React.memo(ConversationList);